import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  atBrowseWorkers, atCheckSession, atStartSession, atEndSession,
  atFavoriteWorker, atUnfavoriteWorker, listWorkforceSkills,
} from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const GREEN = "#1A7A4A";
const RED = "#A32D2D";

/**
 * Contractor-facing browse of the Available Today marketplace.
 * Free public view shows anonymized cards.  Signing in with the email
 * tied to a $149+ subscription unlocks contact info + favorites.
 */
export default function AvailableTodayBrowsePage() {
  const [session, setSession] = useState({ access: false, tier: null, display: null });
  const [signInOpen, setSignInOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [signInError, setSignInError] = useState(null);
  const [signInBusy, setSignInBusy] = useState(false);

  const [skills, setSkills] = useState([]);
  const [filters, setFilters] = useState({
    skill: [], city: "", state: "", max_hourly: "",
    availability: "", language: "", transport: "", q: "",
  });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const p = {};
      if (filters.skill.length) p.skill = filters.skill;
      if (filters.city) p.city = filters.city;
      if (filters.state) p.state = filters.state;
      if (filters.max_hourly) p.max_hourly = Number(filters.max_hourly);
      if (filters.availability) p.availability = filters.availability;
      if (filters.language) p.language = filters.language;
      if (filters.transport) p.transport = filters.transport;
      if (filters.q) p.q = filters.q;
      const r = await atBrowseWorkers(p);
      setItems(r.items || []);
      setTotal(r.total || 0);
      setFavoriteIds(new Set(r.favorite_worker_ids || []));
      setSession(r.access || { access: false });
    } catch (e) {
      setError(e?.message || "Failed to load workers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    listWorkforceSkills().then((d) => setSkills(d.items || [])).catch(() => {});
    atCheckSession().then((s) => setSession(s)).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [JSON.stringify(filters)]);

  const toggleSkill = (name) => {
    setFilters((f) => f.skill.includes(name)
      ? { ...f, skill: f.skill.filter((s) => s !== name) }
      : { ...f, skill: [...f.skill, name] });
  };

  const signIn = async (e) => {
    e?.preventDefault();
    setSignInBusy(true); setSignInError(null);
    try {
      const r = await atStartSession(emailInput.trim());
      if (r.access) {
        setSession(r);
        setSignInOpen(false);
        setEmailInput("");
        load();
      } else {
        setSignInError(r.message || "Access denied.");
      }
    } catch (e) {
      setSignInError(e?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSignInBusy(false);
    }
  };

  const signOut = async () => {
    await atEndSession();
    setSession({ access: false });
    load();
  };

  const toggleFav = async (worker) => {
    if (!session.signed_in && !session.access) {
      setSignInOpen(true);
      return;
    }
    try {
      if (favoriteIds.has(worker.id)) {
        await atUnfavoriteWorker(worker.id);
        const s = new Set(favoriteIds); s.delete(worker.id); setFavoriteIds(s);
      } else {
        await atFavoriteWorker(worker.id);
        setFavoriteIds(new Set([...favoriteIds, worker.id]));
      }
    } catch (e) {
      if (e?.response?.status === 401) setSignInOpen(true);
    }
  };

  const authed = session.access || session.signed_in;

  return (
    <div style={pageWrap} data-testid="available-today-browse-page">
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={topBar}>
          <div>
            <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>NEARNOW · AVAILABLE TODAY · WORKFORCE MARKETPLACE</div>
            <h1 style={h1}>WORKERS AVAILABLE RIGHT NOW</h1>
            <p style={{ color: "#5b5e69", fontSize: 15, maxWidth: 620 }}>
              A live directory of local tradespeople and general labor who are ready to work today. Free browse for everyone — contact info + favorites unlock at the <strong>$149 Pro</strong> or <strong>$299 Growth</strong> plan.
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            {authed ? (
              <div>
                <div style={{ color: GREEN, fontWeight: 700, letterSpacing: 1, fontSize: 12 }}>● SIGNED IN · {session.display || session.tier}</div>
                <Link to="/available-today/requests/new" data-testid="post-request-cta"
                      style={{ display: "inline-block", background: GOLD, color: NAVY, padding: "10px 16px", borderRadius: 10, textDecoration: "none", fontWeight: 700, letterSpacing: 1.5, fontSize: 13, fontFamily: "Bebas Neue, Arial, sans-serif", marginTop: 6 }}>
                  + POST A LABOR REQUEST
                </Link>
                <div style={{ marginTop: 6 }}>
                  <Link to="/available-today/requests" data-testid="my-requests-link" style={{ color: NAVY, fontSize: 12, textDecoration: "none", fontWeight: 700, letterSpacing: 1 }}>
                    MY REQUESTS →
                  </Link>
                  <button onClick={signOut} data-testid="at-signout-button" style={ghostBtn}>Sign out</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setSignInOpen(true)} data-testid="at-signin-button" style={primaryBtn}>
                SIGN IN TO UNLOCK CONTACT
              </button>
            )}
            <div style={{ marginTop: 10 }}>
              <Link to="/available-today/join" data-testid="worker-signup-cta" style={{ color: NAVY, fontSize: 13, fontWeight: 700, letterSpacing: 1, textDecoration: "none" }}>
                ARE YOU A WORKER? JOIN FREE →
              </Link>
            </div>
          </div>
        </div>

        {signInOpen && (
          <form onSubmit={signIn} style={signInCard} data-testid="at-signin-form">
            <div style={{ color: NAVY, letterSpacing: 2, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>ENTER THE EMAIL ON YOUR NEARNOW SUBSCRIPTION</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <input type="email" required autoFocus value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                     placeholder="you@yourcompany.com" data-testid="at-signin-email"
                     style={{ ...inputStyle, flex: "1 1 240px" }} />
              <button type="submit" disabled={signInBusy || !emailInput} data-testid="at-signin-submit" style={{ ...primaryBtn, opacity: signInBusy ? 0.6 : 1 }}>
                {signInBusy ? "Checking…" : "UNLOCK →"}
              </button>
              <button type="button" onClick={() => setSignInOpen(false)} style={ghostBtn}>Cancel</button>
            </div>
            {signInError && <div style={errBox} data-testid="at-signin-error">{signInError} <Link to="/pricing" style={{ color: NAVY, fontWeight: 700 }}>See plans →</Link></div>}
          </form>
        )}

        <div style={filterBar}>
          <div style={{ fontSize: 12, letterSpacing: 2, fontWeight: 700, color: "#5b5e69", marginBottom: 10 }}>FILTER · {total} matching workers</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }} data-testid="skill-filter-pills">
            {skills.map((s) => (
              <button key={s.id} onClick={() => toggleSkill(s.name)} data-testid={`filter-skill-${s.slug}`}
                style={{
                  padding: "8px 12px", borderRadius: 20,
                  border: `1.5px solid ${filters.skill.includes(s.name) ? NAVY : "#d0d3da"}`,
                  background: filters.skill.includes(s.name) ? NAVY : "#fff",
                  color: filters.skill.includes(s.name) ? GOLD : NAVY,
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                }}>
                {s.name}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input placeholder="Search…" value={filters.q} onChange={(e) => setFilters({...filters, q: e.target.value})} style={{...inputStyle, maxWidth: 220}} data-testid="filter-q" />
            <input placeholder="City" value={filters.city} onChange={(e) => setFilters({...filters, city: e.target.value})} style={{...inputStyle, maxWidth: 160}} data-testid="filter-city" />
            <input placeholder="State" value={filters.state} onChange={(e) => setFilters({...filters, state: e.target.value.toUpperCase().slice(0,2)})} style={{...inputStyle, maxWidth: 80}} data-testid="filter-state" />
            <input type="number" placeholder="Max $/hr" value={filters.max_hourly} onChange={(e) => setFilters({...filters, max_hourly: e.target.value})} style={{...inputStyle, maxWidth: 130}} data-testid="filter-max-hourly" />
            <select value={filters.availability} onChange={(e) => setFilters({...filters, availability: e.target.value})} style={{...inputStyle, maxWidth: 180}} data-testid="filter-availability">
              <option value="">Any availability</option>
              <option value="today">Available today</option>
              <option value="this_week">This week</option>
              <option value="weekends">Weekends</option>
              <option value="evenings">Evenings</option>
            </select>
            <input placeholder="Language" value={filters.language} onChange={(e) => setFilters({...filters, language: e.target.value})} style={{...inputStyle, maxWidth: 140}} data-testid="filter-language" />
          </div>
        </div>

        {loading && <div style={{ padding: 40, textAlign: "center", color: "#5b5e69" }}>Loading workers…</div>}
        {error && <div style={errBox}>{error}</div>}
        {!loading && items.length === 0 && (
          <div style={{ padding: 60, textAlign: "center", color: "#5b5e69", background: "#fff", borderRadius: 12 }} data-testid="no-workers">
            No workers match those filters right now. Try widening your search.
          </div>
        )}

        <div style={grid} data-testid="worker-cards">
          {items.map((w) => (
            <WorkerCard key={w.id} w={w}
                        favorited={favoriteIds.has(w.id)}
                        onToggleFav={() => toggleFav(w)}
                        authed={authed} />
          ))}
        </div>
      </div>
    </div>
  );
}

const WorkerCard = ({ w, favorited, onToggleFav, authed }) => (
  <div style={cardStyle} data-testid={`worker-card-${w.id}`}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
      <div>
        <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 22, color: NAVY, letterSpacing: 1 }} data-testid={`worker-name-${w.id}`}>{w.name}</div>
        <div style={{ color: "#5b5e69", fontSize: 12 }}>
          {w.city}{w.state ? `, ${w.state}` : ""}
          {w.years_experience ? ` · ${w.years_experience} yrs` : ""}
        </div>
      </div>
      <button onClick={onToggleFav} data-testid={`fav-toggle-${w.id}`}
              title={favorited ? "Remove from favorites" : "Save to favorites"}
              style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 22, color: favorited ? GOLD : "#c7cad4" }}>
        ★
      </button>
    </div>

    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, margin: "10px 0" }}>
      {(w.skills || []).slice(0, 5).map((s) => (
        <span key={s} style={{ background: "#EFF1F5", color: NAVY, padding: "3px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700 }}>{s}</span>
      ))}
    </div>

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
      <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 22, color: NAVY }}>
        ${w.hourly_rate_usd || "—"}<span style={{ fontSize: 12, color: "#5b5e69" }}>/hr</span>
      </div>
      <div style={{ fontSize: 11, color: w.available_today ? GREEN : "#5b5e69", fontWeight: 700, letterSpacing: 1 }}>
        {w.available_today ? "● AVAILABLE TODAY" : "OFFLINE"}
      </div>
    </div>

    <div style={{ borderTop: "1px solid #f0f2f5", paddingTop: 8, fontSize: 12, color: "#5b5e69" }}>
      {w.max_travel_miles ? `${w.max_travel_miles} mi radius · ` : ""}
      {w.transport ? `${w.transport.replace("_", " ")} · ` : ""}
      {(w.languages || []).join(", ")}
    </div>

    {(w.availability_buckets || []).length > 0 && (
      <div style={{ marginTop: 6, fontSize: 11, color: NAVY, fontWeight: 700, letterSpacing: 1 }}>
        {w.availability_buckets.map((b) => b.replace("_", " ")).join(" · ").toUpperCase()}
      </div>
    )}

    {authed && (w.phone || w.email) ? (
      <div style={{ marginTop: 12, padding: 10, background: NAVY, borderRadius: 10 }} data-testid={`worker-contact-${w.id}`}>
        {w.phone && <div style={{ color: GOLD, fontSize: 13, fontWeight: 700 }}>📞 <a href={`tel:${w.phone}`} style={{ color: GOLD, textDecoration: "none" }}>{w.phone}</a></div>}
        {w.email && <div style={{ color: GOLD, fontSize: 12, marginTop: 2 }}>✉ <a href={`mailto:${w.email}`} style={{ color: GOLD, textDecoration: "none" }}>{w.email}</a></div>}
      </div>
    ) : (
      <div style={{ marginTop: 12, padding: 10, background: "#F5F7FA", borderRadius: 10, textAlign: "center", fontSize: 12, color: NAVY, fontWeight: 700, letterSpacing: 1 }} data-testid={`worker-contact-locked-${w.id}`}>
        🔒 CONTACT INFO — SIGN IN TO REVEAL
      </div>
    )}

    <div style={{ marginTop: 8, fontSize: 10, color: "#9ca3af", letterSpacing: 1 }}>
      NOT YET VERIFIED · PHASE 3 ADDS ID + LICENSE CHECKS
    </div>
  </div>
);

const pageWrap = { background: "#F8F9FA", minHeight: "100vh", padding: "40px 20px" };
const topBar = { display: "flex", justifyContent: "space-between", alignItems: "start", gap: 20, flexWrap: "wrap", marginBottom: 20 };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 48, color: NAVY, margin: "6px 0 14px" };
const signInCard = { background: "#fff", border: `2px solid ${GOLD}`, borderRadius: 14, padding: "18px 20px", marginBottom: 18 };
const filterBar = { background: "#fff", padding: "18px 20px", borderRadius: 12, border: "1px solid #e5e7ee", marginBottom: 20 };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 };
const cardStyle = { background: "#fff", padding: "16px 18px", borderRadius: 14, border: "1px solid #e5e7ee", boxShadow: "0 8px 16px -8px rgba(10,22,40,0.08)" };
const primaryBtn = { background: GOLD, color: NAVY, border: "none", padding: "12px 18px", borderRadius: 10, fontWeight: 700, letterSpacing: 1.5, cursor: "pointer", fontSize: 13, fontFamily: "Bebas Neue, Arial, sans-serif" };
const ghostBtn = { background: "transparent", color: NAVY, border: `1px solid ${NAVY}`, padding: "10px 16px", borderRadius: 10, fontWeight: 700, letterSpacing: 1, cursor: "pointer", fontSize: 12, marginLeft: 8 };
const inputStyle = { padding: "10px 12px", border: "1px solid #d0d3da", borderRadius: 8, fontSize: 14, outline: "none", background: "#fff", flex: "1 1 auto" };
const errBox = { color: RED, background: "#FDECEC", padding: "10px 14px", borderRadius: 10, marginTop: 10, fontSize: 14 };
